import * as React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import IncidentCard from '@components/incidentCard/IncidentCard'
import IncidentListHeaderMobile from '@screens/incident/IncidentListHeaderMobile'
import Drawer from '@components/drawer/Drawer'
import Button from '@components/button/Button'
import IncidentMap from '@components/plan/IncidentMap'
import SortSelector from '@components/modal/SortSelector'
import Loader from '@components/loader/Loader'
import InfiniteList from '@components/list/InfiniteList'
import Modal from '@components/modal/Modal'
import SimpleAlert from '@components/modal/SimpleAlert'
import IncidentFilters from '@screens/incident/filters/IncidentFilters'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'
import * as FamilleStore from '@store/famille/famille'
import * as SocieteStore from '@store/societe/societe'
import * as PlanStore from '@store/plan/plan'
import * as FiltreStore from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import { AuthScope } from '@api/enum'
import { incidentStatus } from '@screens/incident/filters/StatusSelection'
import { RadioOption } from '@components/radio/Radio'

import {
  getActivityItems,
  getCategoryItems,
  getManifestationItems,
  getSpaceAndPackageItems,
  getStatusItems,
} from '@utils/filterItem'

import exporter from '@utils/exporter'

import api from '@api/api'

import Logger from '@utils/logger'
import config from '@app/config'

import useDebounce from '@utils/useDebounce'

import { createEvent, createStore } from 'effector'

const setResults = createEvent<IncidentResponse[]>('setResults')

export const ResultStore = createStore<IncidentResponse[]>([], { name: 'resultIncident' }).on(
  setResults,
  (s, payload) => payload
)

interface Props {
  externalIncident?: IncidentResponse[]
  onBack?: () => void
}

const ITEM_WIDTH = 370
const ITEM_HEIGHT = 338

const MAX_PDF_EXPORTS = 500

const IncidentListScreen = ({ externalIncident, onBack }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const sorts: RadioOption[] = [
    { label: i18n.t('screens.incidentList.sorts.createdAt'), value: 'creationDate' },
    { label: i18n.t('screens.incidentList.sorts.modifiedAt'), value: 'modificationDate' },
    { label: i18n.t('screens.incidentList.sorts.number'), value: 'numero' },
  ]

  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const siteInfo = useReducer(SiteStore.store, (s) => s.site!)

  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations!)
  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)

  const familles = useReducer(FamilleStore.store, (s) => s.activities)
  const activities = React.useMemo(() => familles.filter((f) => f.enabled), [familles])

  const plans = useReducer(PlanStore.store, (s) => s.plans)

  const filters = useReducer(FiltreStore.store, (s) => s.incident)
  const filtersOpen = useReducer(FiltreStore.store, (s) => s.open)
  const incidentUsers = useReducer(FiltreStore.store, (s) => s.users)

  const results = useReducer(ResultStore, (s) => s)

  const [places, setPlaces] = React.useState<LocalisationResponse[]>([])

  const [pagination, setPagination] = React.useState<PageResponseIncident>()
  const [loadingData, setLoadingData] = React.useState(false)
  const incidents = externalIncident || results

  const [searchText, bounced, setSearchText] = useDebounce('')

  const [exporting, setExporting] = React.useState<'PDF' | 'XLSX'>()
  const [cardHeight, setCardHeight] = React.useState(0)

  const [rootHeight, setRootHeight] = React.useState(0)
  const [headerHeight, setHeaderHeight] = React.useState(0)

  const [sort, setSort] = React.useState(sorts[0])
  const [reverse, setReverse] = React.useState(false)
  const [page, setPage] = React.useState(0)

  const filterRequest: IncidentFilterRequest = React.useMemo(
    () => ({
      siteId: siteInfo.id,
      etatsIncident: filters.status.length > 0 ? filters.status : undefined,
      localisationIds: filters.location.length > 0 ? filters.location : undefined,
      manifestationIds: filters.manifestation.length > 0 ? filters.manifestation : undefined,
      familleIds: filters.activity.length > 0 ? filters.activity : undefined,
      utilisateurIds: filters.user.length > 0 ? filters.user : undefined,
      cree: filters.created,
      suivie: filters.followed,
      plan: filters.isMap,
      recente: filters.isNew,
      recherche: filters.search || undefined,
      typeIncident: filters.category.includes('prestation')
        ? filters.category.includes('infrastructure')
          ? 'TOUS'
          : 'MANIFESTATION'
        : filters.category.includes('infrastructure')
        ? 'INFRASTUCTURE'
        : undefined,
      prioritaire: filters.prioritaire || undefined,

      societeIds: filters.societe.length > 0 ? filters.societe : undefined,
      dateDebut: filters.from,
      dateFin: filters.to,
    }),
    [siteInfo, filters]
  )

  const onEndReached = () => {
    if (!loadingData && pagination && pagination.totalPage > 1 && pagination.pageNumber < pagination.totalPage) {
      setPage((prev) => prev + 1)
    }
  }

  React.useEffect(() => {
    // initial complementary calls
    Promise.all([
      api.incident.followed(),
      api.familles.all(siteInfo.id, true),
      api.localisation.packages(siteInfo.id),
      api.societe.all(siteInfo.id),
    ])
      .then(([followed, activities, places, providers]) => {
        UserStore.actions.setFavoriteIncidents(followed)
        FamilleStore.actions.setActivities(activities)
        setPlaces(places)
        SocieteStore.actions.setServices(providers)
      })
      .catch(Logger.error)

    if (scopes.includes(AuthScope.DemandesPlanLecture)) {
      api.plan.all(siteInfo.id).then(PlanStore.actions.setPlans).catch(Logger.error)
    }
  }, [siteInfo.id])

  React.useEffect(() => {
    const knownUserIds = incidentUsers.map((u) => u.id)

    const unknownUserIds = Array.from(new Set(results.map((inc) => inc.utilisateurDeclarantId)))
      .filter(Boolean)
      .filter((id) => !knownUserIds.includes(id))

    if (unknownUserIds.length > 0) {
      Promise.all(unknownUserIds.map(api.users.get))
        .then((newUsers) => FiltreStore.actions.setUsers([...incidentUsers, ...newUsers]))
        .catch(Logger.error)
    }
  }, [results, incidentUsers])

  React.useEffect(() => {
    setPage(0)
  }, [filters, searchText])

  React.useEffect(() => {
    if (
      incidents.length < 20 &&
      pagination &&
      pagination.totalPage > 1 &&
      pagination.pageNumber < pagination.totalPage
    ) {
      setPage((prev) => prev + 1)
    }
  }, [pagination, incidents])

  React.useEffect(() => {
    if (!pagination || page <= pagination.totalPage) {
      setLoadingData(true)

      api.incident
        .searchPage(filterRequest, page, `${sort.value},${reverse ? 'asc' : 'desc'}`)
        .then((list) => {
          if (list.page.pageNumber === 0) {
            setResults(list.incidents)
          } else {
            if (list.incidents.length === 0 && list.page.totalPage > 1 && list.page.pageNumber < list.page.totalPage) {
              // no result but still some page to fetch, go to next page
              setPage((prev) => prev + 1)
            }

            setResults([...results, ...list.incidents])
          }

          setPagination(list.page)
        })
        .catch(() => setResults([]))
        .finally(() => setLoadingData(false))
    }
  }, [siteInfo, filters, sort, reverse, page])

  React.useEffect(() => {
    FiltreStore.actions.setIncidentFilter({ search: bounced })
  }, [bounced])

  const filterItems: FilterItems = React.useMemo(() => {
    return {
      manifestation: getManifestationItems(manifestations),
      activity: getActivityItems(activities),
      location: getSpaceAndPackageItems(places),
      status: getStatusItems(incidentStatus, i18n),
      category: getCategoryItems(i18n),
    }
  }, [manifestations, places, activities, isPhone])

  const openFilterMobile = () => {
    Drawer.open({
      render: () => <IncidentFilters places={places} />,
      hideTarget: true,
    })
  }

  const openIncidentDeclaration = () => navigation.push('/incident/start')

  const exportIncidents = () => {
    SimpleAlert.open({
      title: i18n.t('screens.incidentList.export.title'),
      description: i18n.t('screens.incidentList.export.description', { max: MAX_PDF_EXPORTS }),
      buttons: [
        {
          label: i18n.t('screens.incidentList.export.pdf'),
          onClick: () => {
            SimpleAlert.close()
            setExporting('PDF')
          },
          disabled: !pagination || pagination.totalElement > MAX_PDF_EXPORTS,
        },
        {
          label: i18n.t('screens.incidentList.export.xls'),
          onClick: () => {
            SimpleAlert.close()
            setExporting('XLSX')
          },
        },
      ],
    })
  }

  const renderIncidentCard = (item: IncidentResponse) => (
    <IncidentContainer
      key={item.id}
      activeOpacity={0.9}
      onPress={() => navigation.push(`/incidentDetail/${item.id}`, { incidentId: item.id })}>
      <IncidentCard cardHeight={cardHeight} setCardHeight={assignCardHeight} incident={item} />
    </IncidentContainer>
  )

  React.useEffect(() => {
    if (!!exporting) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({ Content: () => <Loader />, position: 'center' })
        }

        const exportAPI =
          exporting === 'PDF'
            ? exporter.download<IncidentFilterRequest>(
                config.ws.incident.exportPDF,
                'export_incidents.pdf',
                'application/pdf',
                filterRequest
              )
            : exporter.download<IncidentFilterRequest>(
                config.ws.incident.exportXLSX,
                'export_incidents.xlsx',
                'application/vnd.ms-excel',
                filterRequest
              )

        exportAPI
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.incidentList.export.success.title'),
                description: i18n.t('screens.incidentList.export.success.description'),
                buttons: [
                  { label: i18n.t('screens.incidentList.export.success.ok'), onClick: () => SimpleAlert.close() },
                ],
              })
            }
          })
          .catch((error) => {
            Logger.error('Unable to download', error)
            SimpleAlert.open({
              title: i18n.t('screens.incidentList.export.error.title'),
              description: i18n.t('screens.incidentList.export.error.description'),
              buttons: [{ label: i18n.t('screens.incidentList.export.error.ok'), onClick: () => SimpleAlert.close() }],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExporting(undefined)
          })
      }
    }
  }, [exporting, incidents])

  const numberOfFilterSelected = React.useMemo(
    () =>
      Object.entries(filters).reduce((acc, curr, _, tab) => {
        if (curr[0] === 'search' || curr[0] === 'mapLocationId') {
          return acc
        }
        // Filtre date : on ajoute 1 si on a le début, la fin, ou les deux
        else if (curr[0] === 'from') {
          if (!!curr[1]) {
            acc += 1
          }
        } else if (curr[0] === 'to') {
          if (curr[1]) {
            const from = tab.find((t) => t[0] === 'from')
            if (!!from && !from[1]) {
              acc += 1
            }
          }
        } else if (typeof curr[1] !== 'boolean') {
          acc += curr[1].length
        } else {
          if (curr[1]) {
            acc += 1
          }
        }
        return acc
      }, 0),
    [filters]
  )

  const headerLinks = [
    {
      label: i18n.t(`screens.site.sites.${siteInfo.codeLieu}`),
      onClick: () => navigation.push('/'),
    },
    {
      label: i18n.t('screens.incidentList.linkTitle'),
      onClick: FiltreStore.actions.resetIncidentFilter,
    },
  ]

  const startDeclarationButton: HeaderButton = {
    label: i18n.t('screens.incidentList.filters.declareIncidentButton'),
    onClick: openIncidentDeclaration,
  }

  const exportButton: HeaderButton = {
    label: i18n.t('screens.incidentList.filters.exportButton'),
    onClick: exportIncidents,
    color: 'light',
    status: incidents.length === 0 ? 'disabled' : exporting ? 'loading' : 'active',
  }

  const displayExport = scopes.includes(AuthScope.ExportDemandesXlsx)
  const canCreateIncident = !manifestation || !!manifestations.inProgress.find((m) => m.id === manifestation.id)

  const headerButtons: HeaderButton[] = [
    displayExport ? exportButton : undefined,
    canCreateIncident ? startDeclarationButton : undefined,
  ].filter(Boolean) as HeaderButton[]

  const renderHeader = () => {
    if (isPhone) {
      return (
        <IncidentListHeaderMobile
          onSwitchView={onSwitchView}
          exportIncidents={exportButton.onClick}
          setSearch={setSearchText}
          searchText={searchText}
          onLeft={onBack}
          displayExport={displayExport}
          exportStatus={exportButton.status}
        />
      )
    }
  }

  const renderBottomButton = () => {
    if (isPhone && canCreateIncident) {
      return (
        <Footer>
          <Button
            label={i18n.t('screens.incidentList.startIncident')}
            onClick={() => navigation.push('/incident/start')}
            status="active"
          />
        </Footer>
      )
    }
  }

  const assignCardHeight = (height: number) => {
    if (height > cardHeight) {
      setCardHeight(height)
    }
  }
  const onSwitchView = () => {
    if (isPhone) {
      navigation.push('/plan', {
        externalData: incidents,
        externalsFilters: filters,
        filterItems: filterItems,
      })
    }
  }

  if (isPhone && filters.isMap) {
    return (
      <Root
        onLayout={(e) => setRootHeight(e.nativeEvent.layout.height)}
        header={i18n.t('screens.map.title')}
        onLeft={navigation.goBack}
        onRight={openFilterMobile}
        right={!!filterItems ? 'filter' : undefined}>
        {rootHeight > 0 && (
          <IncidentMap
            filtersLocationIds={filters.location}
            incidents={incidents}
            parentHeight={rootHeight}
            plans={plans}
          />
        )}
      </Root>
    )
  }

  return (
    <Root
      header={isPhone ? i18n.t('screens.incidentList.title') : undefined}
      renderSubHeader={isPhone ? undefined : renderHeader}
      renderHeader={isPhone ? renderHeader : undefined}
      renderFixedBottom={renderBottomButton}
      fixed>
      <MainContainer onLayout={(e) => setRootHeight(e.nativeEvent.layout.height)}>
        {!isPhone ? (
          <HeaderContainer onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}>
            <Header
              title={i18n.t('screens.incidentList.title', { count: pagination ? pagination.totalElement : 0 })}
              resizeOn={i18n.t('screens.incidentList.filters.modal.main.title')}
              size={filtersOpen ? 'default' : 'small'}
              onResize={() => FiltreStore.actions.setOpen(!filtersOpen)}
              links={headerLinks}
              buttons={headerButtons}
              renderBottom={() => <IncidentFilters places={places} />}
            />
          </HeaderContainer>
        ) : (
          <>
            <Button
              label={i18n.t('screens.incidentList.buttons.filter', { count: numberOfFilterSelected || '' })}
              onClick={openFilterMobile}
              status="active"
              color="light"
              leftIcon="filter"
            />
            <Count>{i18n.t('screens.incidentList.count', { count: pagination ? pagination.totalElement : 0 })}</Count>
          </>
        )}
        {filters.isMap ? (
          !!rootHeight && (
            <IncidentMap
              filtersLocationIds={filters.location}
              incidents={incidents}
              parentHeight={rootHeight - headerHeight}
              plans={plans}
            />
          )
        ) : (
          <>
            <SortContainer>
              <SortSelector
                onOrderChange={() => {
                  setPage(0)
                  setReverse(!reverse)
                }}
                onSortChange={(opt) => {
                  if (opt) {
                    setPage(0)
                    setSort(opt)
                  }
                }}
                reverse={reverse}
                sorts={sorts}
                sort={sort}
              />
            </SortContainer>
            {loadingData && page === 0 ? (
              <Loading>
                <Loader />
              </Loading>
            ) : incidents.length === 0 ? (
              <MissingInfo>{i18n.t('screens.incidentList.empty')}</MissingInfo>
            ) : (
              <InfiniteList
                data={incidents}
                numColumn={isPhone ? 1 : Math.floor((width - 250) / ITEM_WIDTH)}
                height={ITEM_HEIGHT}
                renderItem={renderIncidentCard}
                onEndReached={onEndReached}
                containerStyle={{
                  marginTop: 20,
                  marginLeft: isPhone ? 0 : 25,
                }}
              />
            )}
          </>
        )}
      </MainContainer>
    </Root>
  )
}

export default IncidentListScreen

const MainContainer = styled(View)`
  flex: 1;
  ${(props) => props.theme.breakpoint === 'phone' && `margin: 18px 16px 0 16px;`}
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px;`}
`

const IncidentContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `padding: 4px;` : `margin-right: 8px;`)};
`

const Footer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const Count = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin: 10px 0;
`
const SortContainer = styled(View)`
  flex-direction: row;
  padding: ${(props) => (props.theme.breakpoint !== 'phone' ? `16px 0 0 16px` : `0px`)};
  margin-bottom: 16px;
`

const HeaderContainer = styled(View)`
  z-index: 10;
`

const Loading = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`
